import React, { useState, useEffect } from 'react';
import { db } from '../Firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { Users, Landmark, TrendingUp, Star, Save, RefreshCw, MapPin, HeartHandshake, Award } from 'lucide-react';
import Swal from 'sweetalert2';

const StatsManager = () => {
  const [stats, setStats] = useState({
    happyMembers: 50000,
    totalDeposits: 500,
    loansDistributed: 1250,
    yearsOfTrust: 28,
    investment: 25000,
    villagesConnected: 150
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Listen to real-time updates from growth_stats/main_stats doc
    const unsub = onSnapshot(doc(db, 'growth_stats', 'main_stats'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setStats({
          happyMembers: Number(data.happyMembers || 50000),
          totalDeposits: Number(data.totalDeposits || 500),
          loansDistributed: Number(data.loansDistributed || data.loansDisbursed || 1250),
          yearsOfTrust: Number(data.yearsOfTrust || 28),
          investment: Number(data.investment || data.activeMembers || 25000),
          villagesConnected: Number(data.villagesConnected || 150)
        });
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleChange = (field, value) => {
    setStats(prev => ({
      ...prev,
      [field]: value === '' ? '' : Number(value)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Show saving loading indicator immediately
      Swal.showLoading();
      
      await setDoc(doc(db, 'growth_stats', 'main_stats'), {
        happyMembers: Number(stats.happyMembers || 50000),
        totalDeposits: Number(stats.totalDeposits || 500),
        loansDistributed: Number(stats.loansDistributed || 1250),
        loansDisbursed: Number(stats.loansDistributed || 1250), // Synchronized
        yearsOfTrust: Number(stats.yearsOfTrust || 28),
        investment: Number(stats.investment || 25000),
        activeMembers: Number(stats.investment || 25000), // Synchronized
        villagesConnected: Number(stats.villagesConnected || 150),
        updatedAt: new Date()
      }, { merge: true });

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Growth stats successfully updated.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to update growth statistics', 'error');
    } finally {
      setSaving(false);
    }
  };

  const statItems = [
    { 
      id: 'happyMembers', 
      label: 'Happy Members / Customers', 
      suffix: '+', 
      icon: Users,
      color: 'text-blue-600 bg-blue-50 border-blue-100',
      description: 'Total active customer/membership base count (shown on Home & About pages).' 
    },
    { 
      id: 'totalDeposits', 
      label: 'Total Deposits (Cr)', 
      suffix: ' Cr+', 
      icon: Landmark,
      color: 'text-emerald-600 bg-emerald-50 border-emerald-100',
      description: 'Total value of customer savings deposits in Crores (shown on Home & About pages).' 
    },
    { 
      id: 'yearsOfTrust', 
      label: 'Years of Trust / Service', 
      suffix: '+', 
      icon: Star,
      color: 'text-purple-600 bg-purple-50 border-purple-100',
      description: 'Years elapsed since foundation establishment in 1995 (shown on Home & About pages).' 
    },
    { 
      id: 'loansDistributed', 
      label: 'Loans Distributed (Cr)', 
      suffix: ' Cr+', 
      icon: Award,
      color: 'text-sky-600 bg-sky-50 border-sky-100',
      description: 'Cumulative loans distributed since establishment (shown on Home & About pages).' 
    },
    { 
      id: 'investment', 
      label: 'Investment (Cr)', 
      suffix: ' Cr+', 
      icon: TrendingUp,
      color: 'text-rose-600 bg-rose-50 border-rose-100',
      description: 'Total investment base of the patsanstha in Crores (shown on Home page).' 
    },
    { 
      id: 'villagesConnected', 
      label: 'Villages Connected', 
      suffix: '+', 
      icon: MapPin,
      color: 'text-teal-600 bg-teal-50 border-teal-100',
      description: 'Total rural villages and township communities connected (shown on Home page).' 
    }
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Growth Statistics Manager</h1>
          <p className="text-gray-500 font-medium">Configure and edit our impact numbers displayed in the About Us page section.</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saving || loading}
          className="btn-primary flex items-center gap-2 shadow-lg shadow-primary/30"
        >
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          Save Growth Stats
        </button>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 md:p-10">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-10 h-10 text-primary animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {statItems.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="p-8 bg-gray-50 rounded-3xl border border-gray-100 flex flex-col group hover:bg-white hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${item.color.split(' ')[1]} ${item.color.split(' ')[0]} ${item.color.split(' ')[2]} group-hover:bg-primary group-hover:text-white group-hover:border-transparent transition-all duration-300`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-primary-dark text-lg leading-tight">{item.label}</h3>
                      <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Growth Stat</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">{item.description}</p>
                  
                  <div className="space-y-2 mt-auto">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest block ml-1">Statistical Number</label>
                    <div className="relative flex items-center">
                      <input 
                        type="number"
                        min="0"
                        value={stats[item.id]}
                        onChange={(e) => handleChange(item.id, e.target.value)}
                        className="w-full bg-white border border-gray-200 pl-4 pr-16 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 font-bold text-gray-800 text-lg shadow-sm"
                        placeholder="e.g. 500"
                      />
                      <span className="absolute right-4 font-bold text-secondary text-sm pointer-events-none select-none">
                        {item.suffix}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsManager;
